/**
 * Quote de postagem para comentários do Instagram
 * Exibe referência visual à postagem original dentro do balão de mensagem
 * Similar ao estilo de citação/reply do WhatsApp
 * 
 * @version 1.0.0
 * @date 2026-01-18
 */

import { useState } from 'react';
import { Image, Film, Images, Instagram } from 'lucide-react';
import { cn } from '@/lib/utils';

// Interface de props do componente
export interface CommentQuoteBubbleProps {
  /** URL da thumbnail da postagem */
  postThumbnail?: string | null;
  /** Tipo de mídia da postagem (IMAGE, VIDEO, REELS, CAROUSEL_ALBUM) */
  postMediaType?: string;
  /** Direção da mensagem (para estilização) */
  direction: 'inbound' | 'outbound';
  /** Callback ao clicar no quote (scroll até postagem) */
  onQuoteClick?: () => void;
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * Retorna ícone apropriado para o tipo de mídia em tamanho pequeno
 */
function getMiniMediaIcon(mediaType?: string) {
  switch (mediaType?.toUpperCase()) {
    case 'VIDEO':
    case 'REELS':
      return <Film className="h-4 w-4" />;
    case 'CAROUSEL_ALBUM':
      return <Images className="h-4 w-4" />;
    default:
      return <Image className="h-4 w-4" />;
  }
}

/**
 * Componente que exibe a referência à postagem dentro do balão do comentário
 * Estilo visual similar ao quote/citação de mensagens
 */
export function CommentQuoteBubble({
  postThumbnail,
  postMediaType,
  direction,
  onQuoteClick,
  className,
}: CommentQuoteBubbleProps) {
  const [imageError, setImageError] = useState(false);
  
  // Define se é inbound ou outbound para cores
  const isInbound = direction === 'inbound';

  return (
    <div
      onClick={onQuoteClick}
      className={cn(
        'flex items-center gap-2 p-1.5 mb-1.5 rounded-lg -mx-1',
        'border-l-3 cursor-pointer transition-colors',
        // Cores baseadas na direção
        isInbound
          ? 'bg-background/40 border-l-amber-500 hover:bg-background/60'
          : 'bg-primary-foreground/10 border-l-amber-300 hover:bg-primary-foreground/15',
        className
      )}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onQuoteClick?.();
        }
      }}
    >
      {/* Mini thumbnail da postagem */}
      <div className="relative h-8 w-8 rounded overflow-hidden bg-muted flex-shrink-0">
        {postThumbnail && !imageError ? (
          <img
            src={postThumbnail}
            alt="Postagem"
            className="h-full w-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className={cn(
            'h-full w-full flex items-center justify-center',
            isInbound 
              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
              : 'bg-amber-200/30 text-amber-100'
          )}>
            {getMiniMediaIcon(postMediaType)}
          </div>
        )}
      </div>

      {/* Label do quote */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <Instagram className={cn(
            'h-3 w-3 flex-shrink-0',
            isInbound 
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-amber-200'
          )} />
          <span className={cn(
            'text-[10px] font-medium truncate',
            isInbound 
              ? 'text-amber-700 dark:text-amber-400'
              : 'text-amber-100'
          )}>
            Comentário na postagem
          </span>
        </div>
      </div>
    </div>
  );
}
